import { consume, provide } from '@lit/context';
import { Task } from '@lit/task';
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ContentDeliveryClient } from '../../clients/content-delivery-client';
import { LearningJourneysClient } from '../../clients/learning-journeys-client';
import { ScormContentClient } from '../../clients/scorm-content-client';
import { SkillsClient } from '../../clients/skills-client';
import { TinCanContentClient } from '../../clients/tincan-content-client';
import type { EnrollmentHelper } from '../../cross-framework-lib/helpers/enrollment-helper';
import type { LupHelper } from '../../cross-framework-lib/helpers/lup-helper';
import { InsightsService } from '../../cross-framework-lib/insights-service/insights-service';
import type { PageMeta } from '../../cross-framework-lib/insights-service/insights.models';
import { i18n } from '../../i18n/i18n';
import { configContext } from '../../providers/config.context';
import { contentDeliveryClientContext } from '../../providers/content-delivery-client.context';
import { learningJourneysClientContext } from '../../providers/learning-journeys-client.context';
import '../../providers/provider';
import { skillsClientContext } from '../../providers/skills-client.context';
import { toastServiceContext } from '../../providers/toast-service.context';
import { ToastService } from '../../services/luds-toast-service';
import { ContainerMixin } from '../../shared/mixins/container.mixin';
import { LoggerMixin } from '../../shared/mixins/logger.mixin';
import {
  ComponentTypes,
  Enrollment,
  ENROLLMENT_STATUSES,
  EnrollmentComponent,
  EntryComponentConfig,
  Module,
  PreloadSettings,
} from '../../shared/types/common.model';
import {
  COURSE_ENROLLMENT_TYPE,
  LearnerActivityNodeType,
  LearnerActivityOtherNodeStates,
  LearnerActivityStatus,
  LEARNING_JOURNEY_CACHED_CONTEXT_KEY,
  LEARNING_JOURNEY_CACHED_CONTEXT_VALUE_KEY,
  LEARNING_JOURNEY_ENROLLMENT_TYPE,
  LearningJourneyCachedContextEntry,
  LearningJourneyContext,
  LearningJourneyState,
} from '../../shared/types/learning-journeys.model';
import { Skill } from '../../shared/types/skills.model';
import { CacheHelper } from '../../shared/util/cache-helper';
import { debounce } from '../../shared/util/debounce';
import { deepMerge } from '../../shared/util/deep-merge';
import { importCrossFrameworkLib } from '../../shared/util/import-cross-framework-lib';
import { safeParseJson } from '../../shared/util/safe-parse-json';
import { isEnrollmentsPageUrl } from '../../shared/util/url-helpers';
import { CompletionItem } from './completion-item';
import './course-player';
import './course-player-view';
import { CoursePlayerState, stateContext } from './course-player.context';
import {
  COURSE_PLAYER_NAV_OPEN,
  COURSE_PLAYER_SET_STATE,
  CoursePlayerEvents,
  SocialMedia,
} from './course-player.model';
import { ANY_ROUTE, COMPLETE_ROUTE, DESCRIPTION_ROUTE, SCORM_CONTENT_ROUTE, SCORM_ROUTE } from './routes';

@customElement('lua-course-player-container')
export class LuaCoursePlayerContainer extends LoggerMixin(ContainerMixin(LitElement)) {
  @consume({ context: contentDeliveryClientContext })
  private contentDeliveryClient: ContentDeliveryClient;

  @consume({ context: toastServiceContext })
  private toastService: ToastService;

  @consume({ context: configContext })
  private internalConfig: EntryComponentConfig;

  @consume({ context: skillsClientContext, subscribe: true })
  skillsClient: SkillsClient;

  @consume({ context: learningJourneysClientContext, subscribe: true })
  learningJourneysClient: LearningJourneysClient;

  /** This is the state of the course player */
  @provide({ context: stateContext })
  @state()
  private _coursePlayerState: CoursePlayerState = {
    /*
     * Initial state
     */

    currentRoute: ANY_ROUTE,
    isCompleted: false,
    enrollment: null,
    courseStructure: [],
    currentModule: null,
    isSidenavOpen: true,
    isSidenavDisabled: false,
    isLoading: true,
    flags: {},
    onDescriptionPage: false,
    isPreview: false,
    settings: {},
    completePage: {
      items: [],
    },
    isTakingExam: false,
    isCompletionViewActive: false,
    showExitModuleButton: false,
    /** Default to true to show the exit course button */
    showExitCourseButton: true,
    openInstructorMessage: false,
    scorm: {
      isInline: false,
      isDebuggerEnabled: false,
      contentUrl: null,
      autoPlay: false,
    },

    canReAttemptByModule: {},

    featureFlags: {},

    /*
     * Actions
     */

    toggleSidenav: this.toggleSidenav.bind(this),
    exitCourse: this.exitCourse.bind(this),
    navigateBackToCourse: this.navigateBackToCourse.bind(this),
    navigateToEditContent: this.navigateToEditContent.bind(this),
    navigateToHome: this.navigateToHome.bind(this),
    navigateToPreviousModule: this.navigateToPreviousModule.bind(this),
    navigateToNextModule: this.navigateToNextModule.bind(this),
    hasNextModule: this.hasNextModule.bind(this),
    hasPreviousModule: this.hasPreviousModule.bind(this),
    navigateToModule: this.navigateToModule.bind(this),
    confirmCompletion: this.confirmCompletion.bind(this),
    navigateToContentDetails: this.navigateToContentDetails.bind(this),
    showDescriptionPage: this.showDescriptionPage.bind(this),
    navigateToCompletePage: this.navigateToCompletePage.bind(this),
    canShowCompletionBar: this.canShowCompletionBar.bind(this),
    getCompletionItems: this.getCompletionItems.bind(this),
    closeSocialLinksModal: this.closeSocialLinksModal.bind(this),
    dispatchShareEvent: this.dispatchShareEvent.bind(this),
    navigateToNextCourse: this.navigateToNextCourse.bind(this),
    leaveModule: this.leaveModule.bind(this),
    launchScorm: this.launchScorm.bind(this),
    showFooterExitCourseButton: this.showFooterExitCourseButton.bind(this),
    navigateToScormContent: this.navigateToScormContent.bind(this),
    validateScormLaunch: this.validateScormLaunch.bind(this),
    toggleInstructorMessage: this.toggleInstructorMessage.bind(this),
    navigateToLearningJourney: this.navigateToLearningJourney.bind(this),
  };

  override connectedCallback() {
    super.connectedCallback();

    window.addEventListener('lua-translations-loaded', (event) => {
      this.logger.info(`Loading new locale: ${event.detail.locale}`);
      this.updateState({ timestamp: Date.now() });
    });

    window.addEventListener(COURSE_PLAYER_SET_STATE, (e: CustomEvent<CoursePlayerState>) => {
      this.updateState(e.detail);
    });

    window.addEventListener('resize', this.resizeWindowHandler);

    this.closeSidenavOnMobile();
  }

  override disconnectedCallback(): void {
    window.removeEventListener('resize', this.resizeWindowHandler);
    super.disconnectedCallback();
  }

  private closeSidenavOnMobile() {
    if (window.innerWidth < 768) {
      this.toggleSidenav(false);
    }
  }

  private resizeWindowHandler = debounce(
    () => {
      this.closeSidenavOnMobile();
    },
    100,
    { leading: true }
  );

  public get coursePlayerState(): CoursePlayerState {
    return this._coursePlayerState;
  }

  private set coursePlayerState(value: CoursePlayerState) {
    this._coursePlayerState = value;
  }

  private updateState(state: Partial<CoursePlayerState>) {
    const prevState = { ...this.coursePlayerState };

    this.coursePlayerState = deepMerge({ ...prevState }, { ...state });

    this.logger.stateChange?.(prevState, state, { ...this.coursePlayerState });
  }

  private hasCertificate() {
    return !!this.coursePlayerState?.enrollment?.certification_id;
  }

  private shouldShareCertificate() {
    return (
      this.coursePlayerState.completePage.social_sharing?.linkedin_is_enabled ||
      this.coursePlayerState.completePage.social_sharing?.twitter_is_enabled
    );
  }

  private viewBadges() {
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.VIEW_BADGES, {
        bubbles: true,
        composed: true,
        detail: {
          awarded_badges: this.coursePlayerState?.completePage?.awarded_badges,
        },
      })
    );
  }

  private downloadCertificate() {
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.DOWNLOAD_CERTIFICATE, {
        bubbles: true,
        composed: true,
        detail: {
          certification_id: this.coursePlayerState.enrollment.certification_id,
          enrollment_id: this.coursePlayerState.enrollment.id,
          for_course: true,
          user_id: this.coursePlayerState.enrollment.user_id,
        },
      })
    );
  }

  dispatchShareEvent(media: SocialMedia) {
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.SOCIAL_LINK_CLICKED, {
        bubbles: true,
        composed: true,
        detail: {
          media,
          enrollment_id: this.coursePlayerState.enrollment.id,
          enrollment_name: this.coursePlayerState.enrollment.name,
          customHashes: this.coursePlayerState.completePage.social_sharing.twitter_hash_tags,
          relatedAccounts: this.coursePlayerState.completePage.social_sharing.twitter_related_accounts,
          status: this.coursePlayerState.enrollment.status_id,
          customUrl: this.coursePlayerState.completePage.social_sharing.twitter_url,
        },
      })
    );
  }

  private openSocialShare() {
    this.updateState({
      completePage: {
        ...this.coursePlayerState.completePage,
        isShareModalOpen: true,
      },
    });
  }

  closeSocialLinksModal() {
    this.updateState({
      completePage: {
        ...this.coursePlayerState.completePage,
        isShareModalOpen: false,
      },
    });
  }

  private hasAwardedBadges() {
    return this.coursePlayerState?.completePage?.awarded_badges?.length > 0;
  }

  private hasAwardedSkills() {
    return this.coursePlayerState?.completePage?.awarded_skills?.length > 0;
  }

  private hasNextCourseOnPath() {
    return !!this.coursePlayerState.nextCourseOnPath;
  }

  private async fetchAwardedSkills(courseGuid: string): Promise<Skill[]> {
    return await this.skillsClient.getSkillsAssociatedWithCourse(courseGuid).catch((e): Skill[] => {
      this.logger.error('Fetching skills failed on course completion', e);
      return [];
    });
  }

  private addAwardedSkillsToItems(items: CompletionItem[]) {
    const skillItems = this.coursePlayerState.completePage.awarded_skills.map((skill) => skill.name).join(', ');
    items.push({
      icon: 'skills',
      title: i18n('lua.courses.completion.new_skill_title'),
      description: `${i18n('lua.courses.completion.new_skill_description')}: <b>${skillItems}</b>`,
      buttons: [
        {
          label: i18n('lua.courses.completion.view'),
          action: () => this.navigateToMyProfileSkills(),
        },
      ],
    });
  }

  private navigateToMyProfileSkills() {
    window.location.href = `${this.internalConfig.baseUrl}/my-profile/skills`;
  }

  /**
   * Safely parses the learning journey context from localStorage
   */
  private parseLearningJourneyContext(raw: string): Record<string, LearningJourneyCachedContextEntry> | null {
    const parsed = safeParseJson<Record<string, LearningJourneyCachedContextEntry>>(raw);
    if (parsed && Object.keys(parsed).length > 0) {
      return parsed;
    }
    return null;
  }

  /**
   * Retrieves the cached ljEnrollmentId for a given courseKey if available and valid
   */
  private getCachedLJEnrollmentId(
    courseKey: string,
    ljContext: Record<string, LearningJourneyCachedContextEntry>
  ): string | null {
    return CacheHelper.getCachedValue(courseKey, ljContext, LEARNING_JOURNEY_CACHED_CONTEXT_VALUE_KEY);
  }

  /**
   * Updates the cached ljEnrollmentId for a given courseKey and persists to localStorage.
   */
  private updateCachedLJEnrollmentId(
    courseKey: string,
    ljContext: Record<string, LearningJourneyCachedContextEntry>,
    newId: string,
    localStorageKey: string
  ): void {
    CacheHelper.updateCachedValue(
      courseKey,
      ljContext,
      LEARNING_JOURNEY_CACHED_CONTEXT_VALUE_KEY,
      newId,
      localStorageKey
    );
  }

  /**
   * Fetches the learning journey context for a user and course enrollment.
   * Attempts to use cached ljEnrollmentId from localStorage if available and valid.
   * Falls back to fetching context by course enrollment if not found or invalid.
   * Handles errors gracefully and provides user feedback.
   */
  private async fetchLearningJourneyContext(
    userId: number,
    courseEnrollmentId: number
  ): Promise<LearningJourneyContext | null> {
    const localStorageKey = LEARNING_JOURNEY_CACHED_CONTEXT_KEY;
    const courseKey = String(courseEnrollmentId);
    let ljContext: Record<string, LearningJourneyCachedContextEntry> | null = null;

    const ljContextRaw = localStorage.getItem(localStorageKey);
    if (ljContextRaw) {
      ljContext = this.parseLearningJourneyContext(ljContextRaw);
    }

    if (ljContext) {
      const cachedLJEnrollmentId = this.getCachedLJEnrollmentId(courseKey, ljContext);
      if (cachedLJEnrollmentId) {
        try {
          const response = await this.learningJourneysClient.getLearningJourneyContext(
            userId,
            LEARNING_JOURNEY_ENROLLMENT_TYPE,
            cachedLJEnrollmentId
          );
          // If the external_enrollment_id matches, update cache and return
          if (response?.external_enrollment_id && cachedLJEnrollmentId === response.external_enrollment_id) {
            this.updateCachedLJEnrollmentId(courseKey, ljContext, response.external_enrollment_id, localStorageKey);
          }
          return response;
        } catch (error) {
          this.logger.error('Failed to fetch LJ context with cached ljEnrollmentId', error);
        }
      }
    }

    // Fallback: fetch context by course enrollment
    try {
      return await this.learningJourneysClient.getLearningJourneyContext(
        userId,
        COURSE_ENROLLMENT_TYPE,
        courseEnrollmentId
      );
    } catch (error) {
      this.logger.error('LearningJourneyContext API error:', error, { userId, courseEnrollmentId });
      return null;
    }
  }

  getCompletionItems() {
    const items: CompletionItem[] = [];

    if (this.hasCertificate()) {
      const certificateItem: CompletionItem = {
        icon: 'certificate',
        title: i18n('lua.courses.completion.new_certificate.title'),
        description: i18n('lua.courses.completion.new_certificate.description'),
        buttons: [
          {
            label: i18n('lua.courses.completion.download'),
            action: () => this.downloadCertificate(),
          },
        ],
      };

      if (this.shouldShareCertificate()) {
        certificateItem.buttons.unshift({
          label: i18n('lua.courses.completion.share'),
          action: () => this.openSocialShare(),
        });
      }

      items.push(certificateItem);
    }

    if (this.hasAwardedBadges()) {
      items.push({
        icon: 'achievement',
        title: i18n('lua.courses.completion.new_learning_badge.title'),
        description: i18n('lua.courses.completion.new_learning_badge.description'),
        buttons: [
          {
            label: i18n('lua.courses.completion.view'),
            action: () => this.viewBadges(),
          },
        ],
      });
    }

    if (this.hasAwardedSkills()) {
      this.addAwardedSkillsToItems(items);
    }

    if (this.hasNextCourseOnPath()) {
      items.push({
        icon: 'new-course',
        title: i18n('lua.courses.completion.new_course_available.title'),
        description: i18n('lua.courses.completion.new_course_available.description'),
      });
    }

    if (this.hasLearningJourneyContext()) {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldDelay = urlParams.get('confirm_course_completion') === 'true';
      if (!this.isCompletionItemLoading && !this.hasLoadedCompletionItem) {
        this.showLearningJourneyCompletionItemWithSpinner(shouldDelay ? 5000 : null);
      }
    }

    return items;
  }

  /** Toggle state of sidenav */
  private toggleSidenav(value = !this.coursePlayerState.isSidenavOpen) {
    localStorage.setItem(COURSE_PLAYER_NAV_OPEN, JSON.stringify(value));
    this.updateState({ isSidenavOpen: value });
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.TOGGLE_SIDENAV, {
        bubbles: true,
        composed: true,
        detail: value,
      })
    );
  }

  private toggleInstructorMessage(value: boolean) {
    this.updateState({ openInstructorMessage: value });
  }

  /* Redirect users out of the course player */
  private exitCourse() {
    // If admin is previewing course, exit action should redirect him back to course
    if (this.coursePlayerState.isPreview) {
      this.coursePlayerState.navigateBackToCourse();
      return;
    }

    this.dispatchEvent(new Event(CoursePlayerEvents.EXIT_COURSE, { bubbles: true, composed: true, cancelable: true }));
  }

  /* Redirect users to homepage */
  private navigateToHome() {
    // If admin is previewing course, exit action should redirect him back to course
    if (this.coursePlayerState.isPreview) {
      this.coursePlayerState.navigateBackToCourse();
      return;
    }

    this.dispatchEvent(
      new Event(CoursePlayerEvents.NAVIGATE_TO_HOME, { bubbles: true, composed: true, cancelable: true })
    );
  }

  navigateToNextCourse(nextCourseEnrollmentId?: number) {
    const courseEnrollmentId = nextCourseEnrollmentId ?? this.coursePlayerState.nextCourseOnPath;
    const event = new CustomEvent(CoursePlayerEvents.NEXT_COURSE, {
      detail: {
        course_id: courseEnrollmentId,
      },
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    this.dispatchEvent(event);

    if (!event.defaultPrevented) {
      window.location.href = `${this.internalConfig.baseUrl}/enrollments/${courseEnrollmentId}`;
    }
  }

  /* Redirect users back to the course page in preview mode*/
  private async navigateBackToCourse() {
    const course_id = this.coursePlayerState.enrollment?.course_id;
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.BACK_TO_COURSE, {
        detail: {
          course_id,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  /** Navigate to the edit content page */
  private async navigateToEditContent() {
    const { course_id } = this.coursePlayerState.enrollment;
    const component_id = this.coursePlayerState.currentModule.component_id;

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.EDIT_CONTENT, {
        detail: {
          course_id,
          component_id,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  private navigateToCompletePage() {
    // Update url to show enrolment only
    this.updateBrowserUrl(this.coursePlayerState.enrollment?.id);
    // Update state to show completion page
    this.updateState({ currentRoute: COMPLETE_ROUTE, isCompletionViewActive: true });
  }

  /**
   * Navigates to the SCORM content after validation
   * @param isDebuggerEnabled - Whether the SCORM debugger should be enabled
   */
  private async navigateToScormContent(isDebuggerEnabled: boolean) {
    // First validate if we can launch the SCORM content
    const isValid = await this.validateScormLaunch();

    if (!isValid) {
      return; // Exit early if validation fails
    }

    this.updateState({
      currentRoute: SCORM_CONTENT_ROUTE,
      scorm: {
        ...this.coursePlayerState.scorm,
        isDebuggerEnabled,
      },
    });
  }

  private leaveModule() {
    const moduleDetails = this.coursePlayerState.currentModule;

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.LEAVE_MODULE, {
        detail: {
          ...moduleDetails,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  /** Prerender pages from course structure using Speculation Rules API */
  private addPrerender(courseStructure: Module[]) {
    if (HTMLScriptElement?.supports?.('speculationrules')) {
      const specScript = document.createElement('script');
      specScript.type = 'speculationrules';

      const urls = courseStructure.map((module) => `/enrollments/${module.enrollment_id}/page/${module.id}`);

      const specRules = {
        prerender: [
          {
            urls: urls,
            eagerness: 'immediate',
          },
        ],
      };
      specScript.textContent = JSON.stringify(specRules);

      document.body.append(specScript);

      this.logger.info('added speculation rules to', urls);
    }
  }

  hasNextModule() {
    const currentModuleIndex = this.getCurrentModuleIndex();

    return currentModuleIndex < this.coursePlayerState.courseStructure.length - 1;
  }

  hasPreviousModule() {
    // If the course is completed and the completion page is active,
    // show the "Previous module" CTA to allow navigation through the completed course.
    if (this.coursePlayerState.isCompleted && this.coursePlayerState.isCompletionViewActive) {
      return true;
    }

    const currentModuleIndex = this.getCurrentModuleIndex();

    return currentModuleIndex > 0;
  }

  /** Confirm completion of the course */
  private async confirmCompletion() {
    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.COURSE_COMPLETED, {
        detail: {
          module: this.coursePlayerState.currentModule,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  /* Navigate to the previous module in the course */
  private async navigateToPreviousModule() {
    const currentModuleIndex = this.getCurrentModuleIndex();
    let previousModule = this.coursePlayerState.courseStructure[currentModuleIndex - 1];

    // If course is completed we are displaying completion page
    // And no course module is selected, show "Previous module" CTA to allow user to navigate through completed course
    if (this.coursePlayerState.isCompleted && this.coursePlayerState.isCompletionViewActive) {
      previousModule = this.coursePlayerState.courseStructure[this.coursePlayerState.courseStructure.length - 1];
    } else if (!this.canNavigateToModule(previousModule.id)) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.PREVIOUS_MODULE, {
        detail: {
          module: previousModule,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  /* Navigate to the previous module in the course */
  private navigateToNextModule() {
    const currentModuleIndex = this.getCurrentModuleIndex();
    const nextModule = this.coursePlayerState.courseStructure[currentModuleIndex + 1];

    if (!this.canNavigateToModule(nextModule.id)) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.NEXT_MODULE, {
        detail: {
          module: nextModule,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  private navigateToModule(moduleId: number) {
    if (!this.canNavigateToModule(moduleId)) {
      return false;
    }

    const module = this.coursePlayerState.courseStructure.find((module) => module.id === moduleId);

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.GO_TO_MODULE, {
        detail: {
          module,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  private getCurrentModuleIndex() {
    return this.coursePlayerState.courseStructure.findIndex(
      (module) => module?.id === this.coursePlayerState.currentModule?.id
    );
  }

  private async navigateToContentDetails(enrollment_id?: number, course_id?: number, isNextCourse = false) {
    const enrollment = this.coursePlayerState.enrollment;
    const detail = isNextCourse
      ? { enrollment_id, course_id }
      : {
          enrollment_id: enrollment?.id,
          course_id: enrollment?.course_id,
          learning_path_id: enrollment?.learning_path_id,
        };

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.CONTENT_DETAILS, {
        detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
  }

  private async showDescriptionPage() {
    const enrollment_id = this.coursePlayerState.enrollment?.id;
    const event = new CustomEvent(CoursePlayerEvents.SHOW_DESCRIPTION, {
      detail: {
        enrollment_id,
      },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);

    if (!event.defaultPrevented) {
      this.updateState({ onDescriptionPage: true, currentRoute: DESCRIPTION_ROUTE });
    }
  }

  private canNavigateToModule(moduleId: number) {
    const module = this.coursePlayerState.courseStructure.find((module) => module.id === moduleId);

    if (
      !this.enrollmentHelper.moduleReleased(
        module as unknown as EnrollmentComponent, // TODO Is module the same as EnrollmentComponent?
        this.coursePlayerState.settings.enable_module_release_dates
      )
    ) {
      const release_at = new Date(module.release_at).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });

      this.toastService.showToast({
        message: i18n('lua.courses.this_module_will_be_released', { 'placeholders.time': release_at }),
        variant: 'error',
      });
      return false;
    }

    return true;
  }

  canShowCompletionBar() {
    return (
      this.coursePlayerState.currentRoute !== COMPLETE_ROUTE &&
      !this.coursePlayerState.isTakingExam &&
      [ENROLLMENT_STATUSES.PASSED, ENROLLMENT_STATUSES.FAILED, ENROLLMENT_STATUSES.COMPLETED].some(
        (status) => status === this.coursePlayerState.enrollment?.status_id
      )
    );
  }

  /**
   * Validates if a SCORM launch is allowed
   * @returns Promise that resolves to true if launch is allowed, false otherwise
   */
  private async validateScormLaunch(shouldNotify = false): Promise<boolean> {
    if (this.internalConfig.flags.ignoreScormCheck) {
      return true;
    }

    try {
      const currentModule = this.coursePlayerState.currentModule;

      // Check if the module is TinCan (1) or SCORM (0)
      const isTincan = !!currentModule.is_tin_can;
      const isPreview = this.coursePlayerState.isPreview;

      // Payload varies depending on the controller that is rendering the Course Player
      const payloadByPage = {
        preview: { enrollment_id: 0, course_id: this.coursePlayerState.enrollment.course_id },
        enrollment: { enrollment_id: this.coursePlayerState.enrollment.id, course_id: 0 },
      };

      const { enrollment_id, course_id } = payloadByPage[isPreview ? 'preview' : 'enrollment'];

      // Create the appropriate client based on module type
      const scormContentClient = isTincan ? new TinCanContentClient() : new ScormContentClient();

      const scormData = await scormContentClient.getContentData({
        enrollment_id,
        course_id,
        component_id: this.coursePlayerState.currentModule?.component_id,
      });

      // Convert to a boolean indicating if re-attempt is allowed
      const canReAttempt = scormData.alwlaun === 1 || scormData.alwlaun === '1';

      this.updateState({
        canReAttemptByModule: {
          ...this.coursePlayerState.canReAttemptByModule,
          [this.coursePlayerState.currentModule?.component_id]: canReAttempt,
        },
      });

      if (!canReAttempt && shouldNotify) {
        this.toastService.showToast({
          message: i18n('lua.scorm.launched_too_many_times_already'),
          variant: 'error',
        });
        return false;
      }

      return true;
    } catch (error) {
      this.toastService.showToast({
        message: i18n('lua.error.title.unexpected'),
        variant: 'error',
      });
      this.logger.error('Error validating SCORM launch:', error);
      return false;
    }
  }

  /**
   * Validates SCORM launch and then dispatches the SCORM_LAUNCHED event
   * @param isDebuggerEnabled - Whether the SCORM debugger should be enabled
   */
  async launchScorm(isDebuggerEnabled: boolean) {
    // First validate if we can launch the SCORM content
    const isValid = await this.validateScormLaunch();

    if (!isValid) {
      return; // Exit early if validation fails
    }

    this.dispatchEvent(
      new CustomEvent(CoursePlayerEvents.SCORM_LAUNCHED, {
        detail: {
          enableDebugger: isDebuggerEnabled,
          module: this.coursePlayerState.currentModule,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private getNextRoute({
    onDescriptionPage,
    isCompleted,
    currentModule,
    enrollment,
  }: {
    onDescriptionPage?: boolean;
    isCompleted?: boolean;
    currentModule?: EnrollmentComponent;
    enrollment?: Enrollment;
  } = {}): string {
    if (onDescriptionPage) {
      return DESCRIPTION_ROUTE;
    }

    const examModules = [ComponentTypes.exam];
    if (examModules.includes(currentModule.component_type_id)) {
      if (isCompleted && !enrollment.has_attempts_remaining) {
        return COMPLETE_ROUTE;
      } else {
        return ANY_ROUTE;
      }
    }

    const routesByComponentTypes: Partial<Record<ComponentTypes, string>> = {
      [ComponentTypes.scorm]: SCORM_ROUTE,
    };

    if (currentModule.component_type_id in routesByComponentTypes) {
      return routesByComponentTypes[currentModule.component_type_id as ComponentTypes];
    }

    if (isCompleted) {
      return COMPLETE_ROUTE;
    }

    return ANY_ROUTE;
  }

  private lupHelper: LupHelper;
  private enrollmentHelper: EnrollmentHelper;
  private insightsService: InsightsService;

  private importCrossFrameworkLib = new Task(this, {
    task: async () => {
      const { LupHelper, InsightsService, EnrollmentHelper } = await importCrossFrameworkLib();
      this.lupHelper = new LupHelper();
      this.enrollmentHelper = new EnrollmentHelper(this.lupHelper);
      this.insightsService = InsightsService.getInstance();
      this.insightsService.init({
        trackingServiceUrl: '/data-insight/events',
        enabledLogging: true,
      });

      const pageMeta: PageMeta = this.getPageMeta();
      this.insightsService.pageViewEvent(pageMeta);
    },
    args: (): [] => [],
  });

  private getPageMeta(): PageMeta {
    const pageGroup = 'enrollments';

    if (this.coursePlayerState.isPreview) {
      return {
        pagegroup: 'courses',
        pagename: 'preview',
      };
    }

    if (this.coursePlayerState.onDescriptionPage) {
      return {
        pagegroup: pageGroup,
        pagename: 'course_description',
      };
    }

    return {
      pagegroup: pageGroup,
      pagename: 'course_player',
    };
  }

  private updateBrowserUrl(enrollmentId: number, enrollmentOnly = true, moduleId?: number) {
    // enrolment only url is used to show the completion page
    if (enrollmentOnly) {
      const url = `/enrollments/${enrollmentId}`;
      // Update URL without page reload
      window.history.pushState({ enrollmentId }, '', url);
      return;
    }
    // module page url is used to show the module page
    const url = `/enrollments/${enrollmentId}/page/${moduleId}`;
    // Update URL without page reload
    window.history.pushState({ enrollmentId, moduleId }, '', url);
  }

  /**
   * Determines whether the footer exit course button should be shown.
   *
   * This method checks if the course player state is marked as completed,
   * if the current module index is the last module in the course structure,
   * and if the completion view is not active.
   *
   * @returns True if the footer exit course button should be shown, otherwise false.
   */
  showFooterExitCourseButton(): boolean {
    const { courseStructure, isCompletionViewActive, showExitCourseButton } = this.coursePlayerState;
    const currentModuleIndex = this.getCurrentModuleIndex();
    const onLastModule = currentModuleIndex === courseStructure.length - 1;

    return this.coursePlayerState.isCompleted && !isCompletionViewActive && onLastModule && showExitCourseButton;
  }

  private isEnrolmentCompleted(enrollment: Enrollment): boolean {
    return (
      enrollment?.status_id === ENROLLMENT_STATUSES.PASSED ||
      enrollment?.status_id === ENROLLMENT_STATUSES.COMPLETED ||
      enrollment?.status_id === ENROLLMENT_STATUSES.FAILED
    );
  }

  private fetchData = new Task(this, {
    task: async () => {
      const results = await Promise.allSettled([
        this.contentDeliveryClient.getCourseModules(),
        this.contentDeliveryClient.getEnrollmentComponent(),
        this.contentDeliveryClient.getFlags(),
        this.contentDeliveryClient.getEnrollment(),
        this.contentDeliveryClient.getOnDescriptionPage(),
        this.contentDeliveryClient.getIsPreview(),
        this.contentDeliveryClient.getSettings(),
        this.contentDeliveryClient.getWasJustCompleted(),
        this.contentDeliveryClient.getSocialSharing(),
        this.contentDeliveryClient.getAwardedBadges(),
        this.contentDeliveryClient.getNextCourseOnPath(),
        this.contentDeliveryClient.getEmbeddingSettings(),
        this.contentDeliveryClient.getScormSettings(),
        this.contentDeliveryClient.getPortalFeatureFlags(),
      ]);

      // if any of the promises fail, we should log the error
      results.forEach((result) => {
        if (result.status === 'rejected') {
          this.logger.error(result.reason);
        }
      });

      const courseStructure = results[0].status === 'fulfilled' ? results[0].value : [];
      const currentModule = results[1].status === 'fulfilled' ? results[1].value : null;
      const flags = results[2].status === 'fulfilled' ? results[2].value : {};
      const enrollment = results[3].status === 'fulfilled' ? results[3].value : null;
      const onDescriptionPage = results[4].status === 'fulfilled' ? results[4].value : false;
      const isPreview = results[5].status === 'fulfilled' ? results[5].value : false;
      const settings = results[6].status === 'fulfilled' ? results[6].value : {};
      const wasJustCompleted = results[7].status === 'fulfilled' ? results[7].value : false;
      const socialSharing = results[8].status === 'fulfilled' ? results[8].value : {};
      const awardedBadges = results[9].status === 'fulfilled' ? results[9].value : [];
      const nextCourseOnPath = results[10].status === 'fulfilled' ? results[10].value : null;
      const embeddingSettings =
        results[11].status === 'fulfilled'
          ? results[11].value
          : {
              hide_module_sidebar: false,
              embedded_content: false,
              embedded_content_autoplay: false,
            };

      const scormSetting =
        results[12].status === 'fulfilled'
          ? results[12].value
          : {
              tincan_completion_tracking: false,
            };
      const featureFlags = results[13].status === 'fulfilled' ? results[13].value : {};
      const awardedSkills = (settings as PreloadSettings).skills_enabled
        ? await this.fetchAwardedSkills(enrollment.course_guid)
        : [];
      if (this.internalConfig.flags?.isPrerenderEnabled) {
        // NOTE: This experimental feature was disabled by default because there are scenarios where the preemptive requests were moving the course progress forward, even when the user didn't interact with the course.
        this.addPrerender(courseStructure);
      }

      if (!this.learningJourneysClient) {
        this.learningJourneysClient = new LearningJourneysClient(this.internalConfig);
      }

      this.loadLearningJourneyContextTask.run([enrollment.user_id, enrollment.id]);

      const isEnrolmentCompleted = this.isEnrolmentCompleted(enrollment);
      const isCompleted = wasJustCompleted || isEnrolmentCompleted;

      // check if on /description url
      const isDescriptionUrl = window.location.pathname.endsWith('/description');

      // Determine if we're in embedded mode
      const isEmbedded = embeddingSettings?.embedded_content;

      // If course is not completed (if it is completed we show completion page)
      // Set current URL to match selection state (eg. /enrollments/1307110/page/2127021)
      if (!isCompleted && !isDescriptionUrl && !isPreview) {
        this.updateBrowserUrl(enrollment?.id, false, currentModule?.id);
      }

      // Check if user is on enrollments page (eg. /enrollments/1307110) not page one (eg. /enrollments/1307110/page/2127021 )
      let isCompletionViewActive = isCompleted && isEnrollmentsPageUrl();

      // For embedded courses with completed status: always navigate to last module on re-entry
      let effectiveCurrentModule = currentModule;
      if (isCompleted && isEmbedded && isCompletionViewActive && !isDescriptionUrl && !isPreview) {
        // For ALL embedded courses on re-entry, navigate to last module
        const lastModule = courseStructure[courseStructure.length - 1];
        effectiveCurrentModule = lastModule as EnrollmentComponent;
        isCompletionViewActive = false;
        // Update URL to show the last module
        if (effectiveCurrentModule) {
          this.updateBrowserUrl(enrollment?.id, false, effectiveCurrentModule?.id);
        }
      }

      // Determinate which router to load
      const currentRoute = this.getNextRoute({
        onDescriptionPage,
        isCompleted: isCompleted && isCompletionViewActive,
        currentModule: effectiveCurrentModule,
        enrollment,
      });

      this.updateState({
        currentRoute,
        courseStructure,
        isCompletionViewActive,
        currentModule: effectiveCurrentModule,
        flags,
        featureFlags,
        enrollment,
        onDescriptionPage,
        isPreview,
        settings,
        isCompleted,
        nextCourseOnPath,
        completePage: {
          ...this.coursePlayerState.completePage,
          social_sharing: socialSharing,
          awarded_badges: awardedBadges,
          awarded_skills: awardedSkills,
        },
        isLoading: false,
        isSidenavDisabled: embeddingSettings?.embedded_content && embeddingSettings?.hide_module_sidebar,
        showExitCourseButton: !isEmbedded, // Hide exit course button if in LUA mode
        scorm: {
          ...this.coursePlayerState.scorm,
          isInline: scormSetting.tincan_completion_tracking,
          autoPlay: embeddingSettings.embedded_content_autoplay,
        },
      });
    },
    args: (): [] => [],
  });

  private loadLearningJourneyContextTask = new Task<[number, number], void>(this, {
    task: async ([userId, enrollmentId]) => {
      let context = await this.fetchLearningJourneyContext(userId, enrollmentId);
      if (!context?.external_enrollment_id) {
        context = null;
      }

      this.updateState({ learningJourneyContext: context });
    },
  });

  @state()
  private isCompletionItemLoading = false;
  @state()
  private hasLoadedCompletionItem = false;

  /**
   * Call this when the completion page is rendered to start the spinner-then-API flow
   */
  private async showLearningJourneyCompletionItemWithSpinner(delayMs?: number) {
    this.isCompletionItemLoading = true;
    this.hasLoadedCompletionItem = false;
    // Set only spinner item
    this.updateState({
      completePage: {
        ...this.coursePlayerState.completePage,
        items: [this.getSpinnerCompletionItem()],
      },
    });
    if (delayMs) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    // Call API
    try {
      const enrollment = this.coursePlayerState.enrollment;
      const learningJourneyContext = this.coursePlayerState.learningJourneyContext;

      if (!enrollment) return;
      const userId = enrollment.user_id;
      const { external_workflow_id, external_version_id, external_enrollment_id } = learningJourneyContext;

      if (!external_workflow_id) {
        this.logger.error('No ljEnrollmentId found in localStorage', external_enrollment_id);
        this.isCompletionItemLoading = false;
        this.hasLoadedCompletionItem = false;
        return;
      }

      const state = await this.learningJourneysClient.getLearningJourneyState(
        userId,
        external_workflow_id,
        external_version_id,
        external_enrollment_id
      );

      this.updateState({
        completePage: {
          ...this.coursePlayerState.completePage,
          items: [this.getLJCompletionItem(state)],
          learningJourneyState: state,
        },
      });

      this.hasLoadedCompletionItem = true;
      this.isCompletionItemLoading = false;
    } catch (e) {
      this.logger.error('Failed to load learning journey state', e);
      this.isCompletionItemLoading = false;
      this.hasLoadedCompletionItem = false;
    }
  }

  /**
   * Returns a CompletionItem with only a spinner using luds-loader
   */
  private getSpinnerCompletionItem(): CompletionItem {
    return {
      icon: '',
      title: '',
      description: html`<div style="display: flex; justify-content: center; align-items: center; height: 52px;">
        <luds-loader size="small"></luds-loader>
      </div>` as unknown as string,
      buttons: [],
    };
  }

  private getLJCompletionItem(state: LearningJourneyState): CompletionItem {
    const icons = {
      unauthorized: 'unauthorized',
      completed: 'lj-completed',
      newCourse: 'lj-new-course',
      timedOut: 'timed-out',
    };

    if (state.workflow_status === LearnerActivityStatus.BLOCKED) {
      return {
        icon: icons.unauthorized,
        title: i18n('lua.courses.completion.lj.blocked.title'),
        description: i18n('lua.courses.completion.lj.blocked.description'),
        buttons: [],
      };
    }

    if (state.workflow_status === LearnerActivityStatus.DONE) {
      return {
        icon: icons.completed,
        title: '',
        description: i18n('lua.courses.completion.lj.completed'),
        buttons: [
          {
            label: i18n('lua.courses.completion.view'),
            action: () => this.navigateToLearningJourney(),
          },
        ],
      };
    }

    if (
      state.node_type === LearnerActivityNodeType.END &&
      state.node_state === LearnerActivityOtherNodeStates.WAITING_FOR_COURSE_COMPLETION
    ) {
      return {
        icon: icons.newCourse,
        title: i18n('lua.courses.completion.lj.waiting_for_completion.title'),
        description: i18n('lua.courses.completion.lj.waiting_for_completion.description'),
        buttons: [
          {
            label: i18n('lua.courses.completion.view'),
            action: () => this.navigateToLearningJourney(),
          },
        ],
      };
    }

    if (
      state.node_type === LearnerActivityNodeType.CERTIFICATE &&
      state.node_state === LearnerActivityOtherNodeStates.WAITING_FOR_COURSE_COMPLETION
    ) {
      return {
        icon: icons.newCourse,
        title: i18n('lua.courses.completion.lj.waiting_for_completion.certificate.title'),
        description: i18n('lua.courses.completion.lj.waiting_for_completion.certificate.description'),
        buttons: [
          {
            label: i18n('lua.courses.completion.view'),
            action: () => this.navigateToLearningJourney(),
          },
        ],
      };
    }

    if (state.delay_node_metadata?.execute_at) {
      return {
        icon: icons.timedOut,
        title: i18n('lua.courses.completion.lj.delay.title'),
        description: `${i18n('lua.courses.completion.lj.delay.description')} ${new Date(
          state.delay_node_metadata.execute_at
        ).toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}`,
        buttons: [],
      };
    }

    // Use the state argument directly for course_node_metadata
    const { enrollment_id, course_id } = state.course_node_metadata || {};

    return {
      icon: icons.newCourse,
      title: i18n('lua.courses.completion.lj.next_course.title'),
      description: i18n('lua.courses.completion.lj.next_course.description'),
      buttons: [
        {
          label: i18n('lua.courses.completion.view'),
          action: () => this.navigateToContentDetails(enrollment_id, course_id, true),
        },
        {
          label: i18n('lua.courses.completion.lj.next_course.actions.start_now'),
          action: () => this.navigateToNextCourse(enrollment_id),
          variant: 'primary',
        },
      ],
    };
  }

  private hasLearningJourneyContext(): boolean {
    const ctx = this.coursePlayerState.learningJourneyContext;
    return !!ctx && typeof ctx === 'object' && Object.keys(ctx).length > 0;
  }

  /**
   * Navigates to the Learning Journey progression page for the current enrollment/context
   * Safely checks for required context and logs error if missing.
   */
  public navigateToLearningJourney() {
    const { learningJourneyContext, enrollment } = this.coursePlayerState;
    if (!learningJourneyContext || !enrollment) {
      return;
    }
    const { external_enrollment_id, external_workflow_id, external_version_id } = learningJourneyContext;
    const { user_id } = enrollment;
    if (!external_enrollment_id || !external_workflow_id || !external_version_id || !user_id) {
      this.logger.error('Cannot navigate: missing required learning journey identifiers', {
        external_enrollment_id,
        external_workflow_id,
        external_version_id,
        user_id,
      });
      return;
    }
    window.location.href = `${this.internalConfig.baseUrl}/learning-journey-progression/${external_workflow_id}/${external_version_id}/${user_id}/${external_enrollment_id}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-course-player-container': LuaCoursePlayerContainer;
  }

  interface WindowEventMap {
    [CoursePlayerEvents.TOGGLE_SIDENAV]: CustomEvent<boolean>;
    /**
     * Event to update the course player state
     */
    [COURSE_PLAYER_SET_STATE]: CustomEvent<CoursePlayerState>;
    [CoursePlayerEvents.EXIT_COURSE]: Event;
    [CoursePlayerEvents.PREVIOUS_MODULE]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.NEXT_MODULE]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.GO_TO_MODULE]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.COURSE_COMPLETED]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.CONTENT_DETAILS]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.SHOW_DESCRIPTION]: CustomEvent<{ module: unknown }>;
    [CoursePlayerEvents.DOWNLOAD_CERTIFICATE]: CustomEvent<{
      certification_id: number;
      enrollment_id: number;
      for_course: boolean;
      user_id: number;
    }>;
    [CoursePlayerEvents.SOCIAL_LINK_CLICKED]: CustomEvent<{
      media: SocialMedia;
      enrollment_id: number;
      enrollment_name: string;
      customHashes: string;
      relatedAccounts: string;
      status: number;
      customUrl: string;
    }>;
    [CoursePlayerEvents.SCORM_LAUNCHED]: CustomEvent<{ enableDebugger: boolean; module: EnrollmentComponent }>;
    [CoursePlayerEvents.LEAVE_MODULE]: CustomEvent<boolean>;
  }
}
